// Pricing Health — persistent audit card on the Operations Board.
//
// Surfaces classes whose **pricing consistency** would confuse a parent or
// checkout:
//   • Missing structured price (public label suggests a price but no amount is stored)
//   • Custom "Price display" wording that diverges from the structured price
//   • Non-standard billing units (defensive: coerce to per_session)
//
// This audit is scoped strictly to *pricing consistency*. Cover-image
// readiness or other data-integrity checks belong in their own panel.
//
// The card is a pure function of the `classes` prop. When the parent reloads
// the database after a save, the merged list re-renders and this audit
// re-runs synchronously — there is no local snapshot of the previous state.
// After the admin clicks Fix on a specific class, we watch the incoming
// classes prop and toast whether the conflict cleared or still needs
// attention with the exact fields that disagree.

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ClassItem } from "@/data/classes";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import {
  autoFormattedPrice,
  coerceBillingUnit,
  displayMatchesAuto,
  publicPriceLabel,
  type PricingInput,
} from "@/lib/classPricing";

type IssueKind = "missing_price" | "display_mismatch" | "unknown_unit";

type Issue = {
  slug: string;
  title: string;
  dbId?: string;
  kind: IssueKind;
  detail: string;
  publicLabel: string;
  autoLabel: string;
  fields: {
    price_per_child: number | null;
    billing_unit: string;
    price_display: string | null;
    price_display_omits_unit: boolean;
  };
};

export interface PricingHealthCardProps {
  classes: ClassItem[];
  onOpenClass?: (item: ClassItem) => void;
  /** Called after an inline quick-fix writes to the DB; parent should reload. */
  onReload?: () => void | Promise<void>;
}

function buildInput(c: ClassItem): PricingInput {
  return {
    pricePerChild: c.unitPrice ?? 0,
    billingUnit: coerceBillingUnit(c.billingUnit),
    // Prefer the RAW override from the DB; fall back only for legacy items
    // that never had a database row (their `price` is a hand-authored string).
    priceDisplay: c.priceDisplayRaw ?? c.price,
    externalPayment: false,
    hasInstructor: c.hasInstructor,
    displayOmitsUnitApproved: !!c.displayOmitsUnitApproved,
  };
}

export function auditClass(c: ClassItem): Issue[] {
  const out: Issue[] = [];
  if (c.managementType === "external_provider") return out;
  // Classes with no instructor render "Pricing coming soon" by design —
  // don't flag them until an instructor is assigned.
  if (c.hasInstructor === false) return out;
  // Legacy static-only classes (no dbId) can't be fixed through the editor,
  // so keep them out of Pricing Health to avoid unresolvable alerts.
  if (!c.dbId) return out;

  const unit = coerceBillingUnit(c.billingUnit);
  const input = buildInput(c);
  const auto = autoFormattedPrice(input);
  const label = publicPriceLabel(input);
  const fields = {
    price_per_child: c.unitPrice ?? null,
    billing_unit: unit,
    price_display: c.priceDisplayRaw ?? null,
    price_display_omits_unit: !!c.displayOmitsUnitApproved,
  };

  if (String(c.billingUnit ?? "per_session") !== unit) {
    out.push({
      slug: c.id, title: c.title, dbId: c.dbId, kind: "unknown_unit",
      publicLabel: label, autoLabel: auto, fields,
      detail: `Billing unit "${c.billingUnit}" is not recognised — treated as ${unit}.`,
    });
  }

  const hasPrice = Number.isFinite(c.unitPrice) && (c.unitPrice ?? 0) > 0;
  const displayLooksPriced = /\$|free/i.test(label);
  if (!hasPrice && label && !/free/i.test(label) && displayLooksPriced) {
    out.push({
      slug: c.id, title: c.title, dbId: c.dbId, kind: "missing_price",
      publicLabel: label, autoLabel: auto, fields,
      detail: `Public shows "${label}" but no structured price is set.`,
    });
  } else if (hasPrice && !displayMatchesAuto(input)) {
    out.push({
      slug: c.id, title: c.title, dbId: c.dbId, kind: "display_mismatch",
      publicLabel: label, autoLabel: auto, fields,
      detail: `Public shows "${label}" · structured price is ${auto}.`,
    });
  }
  return out;
}

export function PricingHealthCard({ classes, onOpenClass, onReload }: PricingHealthCardProps) {
  const [open, setOpen] = useState(true);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  // Track a class the admin just tried to fix so we can toast the audit
  // result the next time `classes` arrives with fresh DB data.
  const awaitingRef = useRef<{ slug: string; title: string } | null>(null);

  const issues = useMemo<Issue[]>(() => {
    const out: Issue[] = [];
    for (const c of classes) out.push(...auditClass(c));
    return out;
  }, [classes]);

  useEffect(() => {
    const waiting = awaitingRef.current;
    if (!waiting) return;
    const still = issues.find((i) => i.slug === waiting.slug);
    if (still) {
      toast.error("Pricing conflict still needs attention", {
        description: [
          `Public display: ${still.publicLabel || "—"}`,
          `Structured pricing: ${still.autoLabel || "—"}`,
        ].join("\n"),
      });
    } else {
      toast.success("Pricing conflict resolved", {
        description: `${waiting.title} has been removed from Pricing Health.`,
      });
    }
    awaitingRef.current = null;
    // Only react when the audit's issue set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues]);

  const markAwaiting = (iss: Issue) => {
    awaitingRef.current = { slug: iss.slug, title: iss.title };
  };

  const applyDbFix = async (
    iss: Issue,
    patch: Record<string, unknown>,
    optimisticLabel: string,
  ) => {
    if (!iss.dbId) {
      toast.error("This class isn't in the database yet — open the editor to fix it.");
      return;
    }
    setPendingSlug(iss.slug);
    try {
      const { error } = await (supabase as any)
        .from("classes")
        .update(patch)
        .eq("id", iss.dbId);
      if (error) throw error;
      markAwaiting(iss);
      toast.message(optimisticLabel);
      await onReload?.();
    } catch (e) {
      friendlyError({ what: "I couldn't update that class's pricing.", technical: e });
    } finally {
      setPendingSlug(null);
    }
  };

  const total = issues.length;

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between text-left"
        >
          <CardTitle className="flex items-center gap-2 text-base font-heading">
            <DollarSign className="h-4 w-4 text-primary" />
            Pricing Health
            {total > 0 ? (
              <Badge variant="destructive" className="ml-1">{total}</Badge>
            ) : (
              <Badge variant="secondary" className="ml-1">
                <CheckCircle2 className="mr-1 h-3 w-3" /> All clear
              </Badge>
            )}
          </CardTitle>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {total === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every priced class has a structured price and a public label that matches it.
            </p>
          ) : (
            <ul className="space-y-2">
              {issues.map((iss, i) => {
                const isBusy = pendingSlug === iss.slug;
                return (
                  <li
                    key={`${iss.slug}-${iss.kind}-${i}`}
                    className="flex flex-col gap-2 rounded-md border border-border/60 bg-card/60 p-3 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                        <span className="truncate text-sm font-medium">{iss.title}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{iss.detail}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground/80">
                        Fields compared: price · price_display · billing_unit · price_display_omits_unit
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {iss.kind === "display_mismatch" && iss.dbId && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="secondary" disabled={isBusy}>
                              {isBusy ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : null}
                              Quick fix
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-64">
                            <DropdownMenuItem
                              onClick={() =>
                                applyDbFix(
                                  iss,
                                  { price_display: null, price_display_omits_unit: false },
                                  "Clearing custom wording…",
                                )
                              }
                            >
                              Use structured display ({iss.autoLabel})
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                applyDbFix(
                                  iss,
                                  { price_display_omits_unit: true },
                                  "Marking bare price as intentional…",
                                )
                              }
                            >
                              Keep bare display, mark intentional
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {onOpenClass && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={() => {
                            const match = classes.find((c) => c.id === iss.slug);
                            if (!match) return;
                            markAwaiting(iss);
                            onOpenClass(match);
                          }}
                        >
                          Fix in editor
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default PricingHealthCard;
